import parse from 'html-react-parser';
import { sanitize } from 'dompurify';

/**
 * Takes a string and a desired containing keyword, returns html as JSX
 * @param originalString non-modified partial or full text for modification
 * @param keyword string to match against
 * @returns Bolded HTML string as JSX elemenet
 */
const makeBold = (
  originalString: string | undefined,
  keyword: string | undefined,
  withoutHTMLFrom?: boolean,
) => {
  if (
    originalString === undefined ||
    keyword === undefined ||
    _.isEmpty(originalString) ||
    _.isEmpty(keyword)
  )
    return originalString;
  try {
    var escapedRegexKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var reg = new RegExp('(' + escapedRegexKeyword + ')', 'ig');
    return withoutHTMLFrom
      ? originalString.replace(reg, '<strong>$1</strong>')
      : htmlFrom(originalString.replace(reg, '<strong>$1</strong>'));
  } catch (e) {
    return originalString;
  }
};

/**
 * Embolden portions of `originalString` matching any of the supplied `keywords`.
 * @param originalString candidate text
 * @param keyword kewords to embolden
 * @returns a string with any matching keywords wrapped in a <strong> tag. Render as JSX with `htmlFrom`.
 */
const makeAllBold = (originalString: string, keywords: string[]) => {
  // 1. Early return if there are no keywords
  if (!keywords?.length) return originalString;
  // 2. Create a set of unique keywords
  const uniqueKeywords = Array.from(new Set(keywords));
  // 3. Helper to escape special regex characters
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // 4. Use reduce to apply replacements one by one
  return uniqueKeywords.reduce((acc, keyword) => {
    const trimmedKeyword = keyword.trim();
    // Skip empty or whitespace-only keywords
    if (!trimmedKeyword) return acc;
    const regex = new RegExp(`(${escapeRegex(trimmedKeyword)})`, 'ig');
    // Replace all occurrences of the keyword with <strong> wrapped text
    return acc.replace(regex, '<strong>$1</strong>');
  }, originalString);
};

const buildFormattedKeywords = (keywords: Array<string> | undefined) => {
  const formattedKeywords = Array<string>();
  if (keywords) {
    keywords.forEach((value) => {
      if (value) {
        formattedKeywords.push(value.replace(/\D/g, ''));
      }
    });
  }
  return formattedKeywords;
};

const buildRegex = (formattedKeywords: string[]) => {
  let reg = '';
  formattedKeywords.forEach((value, index, array) => {
    if (!_.isEmpty(value)) {
      if (index === array.length - 1) {
        reg += value;
      } else {
        reg += value + '|';
      }
    }
  });
  return reg;
};
const getBoldedText = (
  slimmedString: string,
  formattedKeywords: string[],
  reg: string,
) => {
  // bold the text using regex.
  if (!_.isEmpty(formattedKeywords)) {
    var expression = new RegExp('(' + reg + ')', 'ig');
    // get bolded text if appropriate
    return slimmedString.replace(expression, '<strong>$1</strong>');
  }
  return slimmedString;
};
const hidePhoneWithAsteriskIfNeeded = (
  canCall: boolean,
  boldedText: string,
) => {
  if (!canCall) {
    return boldedText.replace(/^(\D*\d\D*){6}/gm, function (match) {
      return match.replace(/\d/g, '*');
    });
  }
  return boldedText;
};
const makePhoneNumberBoldAndHiddenIfNeeded = (
  canCall: boolean,
  originalPhone: string,
  keywords: Array<string> | undefined,
) => {
  var phoneRegex = new RegExp(
    /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/gm,
  );
  if (phoneRegex.test(originalPhone)) {
    // remove all special characters from the phone number
    let slimmedString = originalPhone.replace(/\D/g, '');

    const formattedKeywords = buildFormattedKeywords(keywords);
    const reg = buildRegex(formattedKeywords);
    const boldedText = getBoldedText(slimmedString, formattedKeywords, reg);
    const indexesOfSeparation = findIndexesOfPhonePattern(boldedText);
    const handledPhoneNumber = hidePhoneWithAsteriskIfNeeded(
      canCall,
      boldedText,
    );

    const formattedPhone = insertTextAtIndices(
      handledPhoneNumber,
      indexesOfSeparation,
    );
    return htmlFrom(formattedPhone);
  } else {
    return originalPhone;
  }
};

/**
 * Returns npa and nxx positions in a phone number string
 * Source string can have any special characters that normally throw off regex pattern matching
 * @param source unformatted phone number
 * @returns object of indexes of dashes to be updated in string
 */
const findIndexesOfPhonePattern = (source: string) => {
  let count = 0;
  let npa = 4;
  let nxx = 11;
  for (let i = 0; i < source.length; i++) {
    if (isNumeric(source[i])) {
      count++;
      if (count == 3) {
        npa = i + 1;
      } else if (count == 6) {
        nxx = i + 1;
      }
    }
  }

  var text = {
    [npa]: '-',
    [nxx]: '-',
  };

  return text;
};

/**
 * Determines if string is numeric only
 * @param str source string
 * @returns true or false if the string is a number
 */
const isNumeric = (str: string) => {
  return /^-?\d+$/.test(str);
};

/**
 * Inserts provided values at their indexes
 * Shifts array to right to account for insertions
 * @param originalString string to manipulate with text indices
 * @param stringInsertions indexes and values to insert into the string
 * @returns string updated with values inserted at indexes
 */
const insertTextAtIndices = (
  originalString: string,
  stringInsertions: Record<number, string>,
): string => {
  return originalString.replace(
    /./g,
    function (character: string, index: number) {
      return stringInsertions[index]
        ? stringInsertions[index] + character
        : character;
    },
  );
};

/**
 * Sanitizes string and returns html as JSX
 * @param htmlString
 * @returns html as converted JSX element
 */
const htmlFrom = (htmlString: string) => {
  const cleanHtmlString = sanitize(htmlString, {
    USE_PROFILES: { html: true },
  });
  const html = parse(cleanHtmlString);
  return html;
};

/**
 * Strips html from string
 * @param htmlString
 * @returns string with html parsed out
 */
const stripHTML = (htmlString: string) => {
  const stripRegex = /(<([^>]+)>)/gi;
  const result = htmlString.replace(stripRegex, '');
  return result;
};

/**
 * Finds contents of the first paragraph tag and strips out html
 * @param htmlString
 * @returns content of first paragraph
 */
const cleanseFirstParagraph = (htmlString: string) => {
  const firstParagraphRegex = /<p>(.*?)<\/p>/;
  const corresp = firstParagraphRegex.exec(htmlString);
  const firstParagraph = corresp ? corresp[1] : '';
  const stripRegex = /(<([^>]+)>)/gi;
  const result = firstParagraph.replace(stripRegex, '');
  return result;
};

export {
  makePhoneNumberBoldAndHiddenIfNeeded,
  makeBold,
  makeAllBold,
  htmlFrom,
  stripHTML,
  cleanseFirstParagraph,
};
